import type {ReactElement} from 'react'
import {Box, merge, type SxProp} from '@primer/react'
import type {BetterSystemStyleObject} from '@primer/react/lib-esm/sx'
import {
  ActionBar as GitHubUiActionBar,
  gapFromDensity,
  type ActionBarProps as GitHubUiActionBarProps,
} from '@github-ui/action-bar'
import type {DensityToggle} from './DensityToggle'
import {testIdProps} from '@github-ui/test-id-props'

// Require actionsLabel if actions are provided, and vice versa.
type ActionsLabelProps =
  | {
      /**
       * Description of the controls found in the action bar, e.g., "Issue actions", "Markdown formatting tools".
       * Will be used in a hidden label for accessibility purposes. Defaults to the title for the ListView.
       */
      actionsLabel: GitHubUiActionBarProps['label']
      /**
       * The controls to show in the action bar. These will move into a dropdown menu when the screen isn't big enough to
       * fit them side by side.
       */
      actions: GitHubUiActionBarProps['actions']
    }
  | {actionsLabel?: undefined; actions?: undefined}

export type ActionBarProps = ActionsLabelProps & {
  /**
   * Include a toggle to switch between Comfortable and Condensed view modes.
   */
  densityToggle?: ReactElement<typeof DensityToggle>
} & SxProp &
  Omit<GitHubUiActionBarProps, 'actions' | 'label'>

const defaultSx: SxProp['sx'] = {
  display: 'flex',
  flexDirection: 'row',
  marginLeft: 'auto',
  alignItems: 'center',
}

export const ActionBar = ({actions, sx, children, density, actionsLabel, densityToggle}: ActionBarProps) => {
  const nonCollapsibleActions = (
    <>
      {children}
      {densityToggle}
    </>
  )

  if (actions && actions.length > 0) {
    return (
      <GitHubUiActionBar
        label={actionsLabel?.trim()}
        actions={actions}
        density={density}
        {...testIdProps('listview-actions')}
        sx={sx}
      >
        {nonCollapsibleActions}
      </GitHubUiActionBar>
    )
  }

  const actionsStyles = sx ? merge<BetterSystemStyleObject>(defaultSx, sx) : defaultSx

  return (
    <Box
      {...testIdProps('listview-actions')}
      sx={merge<BetterSystemStyleObject>(actionsStyles, {gap: gapFromDensity(density)})}
    >
      {nonCollapsibleActions}
    </Box>
  )
}

try{ ActionBar.displayName ||= 'ActionBar' } catch {}